<template>
    <div>
        <div class="container-fluid">
            <div class="row">
                <mobile-recovery
                    id="landing-recovery"
                    class="px-1 mb-2"
                    :recovery-amount-in-cents="savedEstimatedDevice.amountInCents"
                    :recovery-device-model="savedEstimatedDevice.deviceModel"
                    :manual-estimation="manualEstimation"
                    :simulator-modal-id="simulatorModalId"
                    :bonus-recovery-selector-link="dto.bonusRecoveryDetails.link"
                    :bonus-recovery-selector-link-label="dto.bonusRecoveryDetails.linkLabelLong"
                    :messages="dto.mobileRecovery.messages"
                    :visual="dto.mobileRecovery.visual"
                    :shop="dto.shop"
                />
                <mobile-recovery-simulator-modal
                    :modal-id="simulatorModalId"
                    :shop="dto.shop"
                    :messages="dto.recoverySimulator"
                    @update-recovery-simulation="updateRecoveryPrice"
                />
                <mobile-recovery-simulation-result-modal
                    :modal-id="currentSimulationResultModalId"
                    :shop="dto.shop"
                    :messages="dto.recoverySimulator.result"
                    :estimated-device-price-in-cents="estimatedDevice.amountInCents"
                    :estimated-device-model="estimatedDevice.deviceModel"
                    @new-simulation="newSimulation"
                    @save-simulation="saveSimulation"
                />
            </div>

            <h2 v-html="dto.recoveryDetails.infosRecovery.title" />
            <list-icon
                id="list-icon-landing-recovery"
                class="mb-1"
                additional-item-classes="text-dark fw-normal"
                element-type="ol"
                :items="dto.recoveryDetails.infosRecovery.processItems"
                highlight
                no-border-bottom
            />

            <ob1-expanding-accordion
                class="bg-gray2 px-1 mb-1"
                element-type="h3"
                :link-title="dto.recoveryDetails.storeRecovery.title"
                link-id="store-button"
                collapsed-block-id="store-detail"
            >
                <list-chronological-order
                    id="list-chronological-landing-store-recovery"
                    :items="dto.recoveryDetails.storeRecovery.stepList"
                    :no-border-bottom="true"
                />
                <p v-msh-html="dto.recoveryDetails.storeRecovery.processTitle" class="mb-0" />
                <p id="store-details" v-msh-html="dto.recoveryDetails.storeRecovery.processDetail" class="mb-0" />
                <div class="d-flex justify-content-center text-mw">
                    <ob1-liens
                        is-button
                        :with-arrow="true"
                        :modal-id="simulatorModalId"
                        data-bs-toggle="modal"
                        :text="dto.recoveryDetails.infosRecovery.estimationButtonLabel"
                        :accessibility-text="dto.recoveryDetails.infosRecovery.estimationButtonAccessibilityLabel"
                        data-oevent-label="vers_simulateur"
                        data-oevent-category="menu_reprise_en_boutique"
                        data-oevent-action="j_accede_au_simulateur  "
                    />
                </div>
            </ob1-expanding-accordion>

            <ob1-expanding-accordion
                class="bg-gray2 px-1 mb-2"
                element-type="h3"
                :link-title="dto.recoveryDetails.onlineRecovery.title"
                link-id="online-button"
                collapsed-block-id="online-detail"
            >
                <list-chronological-order
                    id="list-chronological-landing-online-recovery"
                    :items="dto.recoveryDetails.onlineRecovery.stepList"
                    :no-border-bottom="true"
                />
                <p v-msh-html="dto.recoveryDetails.onlineRecovery.processTitle" class="mb-0" />
                <p id="online-details" v-msh-html="dto.recoveryDetails.onlineRecovery.processDetail" class="mb-0" />
                <p v-msh-html="dto.recoveryDetails.onlineRecovery.mention" />
                <div class="d-flex justify-content-center text-mw">
                    <ob1-liens
                        is-button
                        :with-arrow="true"
                        :modal-id="simulatorModalId"
                        data-bs-toggle="modal"
                        :text="dto.recoveryDetails.infosRecovery.estimationButtonLabel"
                        :accessibility-text="dto.recoveryDetails.infosRecovery.estimationButtonAccessibilityLabel"
                        data-oevent-label="vers_simulateur"
                        data-oevent-category="menu_reprise_en_ligne"
                        data-oevent-action="j_accede_au_simulateur"
                    />
                </div>
            </ob1-expanding-accordion>

            <h2 v-html="dto.bonusRecoveryDetails.title" />
            <div class="row pb-1 mb-2 justify-content-center">
                <div
                    class="col-12 col-md-8 px-0"
                    :class="isSoshShop ? 'bg-primary3' : 'bg-greige'"
                >
                    <div class="position-absolute">
                        <ob1-tag :label="dto.bonusRecoveryDetails.tag" is-aria-hidden />
                    </div>
                    <div class="row justify-content-center">
                        <div class="col-8 pb-2 px-1" :class="isSoshShop ? 'pt-1' : 'pt-4'">
                            <div class="visual text-center">
                                <img :src="dto.bonusRecoveryDetails.visual" alt="">
                            </div>
                            <div
                                class="h4 pt-1"
                                :class="isSoshShop ? 'text-white' : ''"
                                :aria-label="dto.bonusRecoveryDetails.promiseAccessibilityLabel"
                                v-html="dto.bonusRecoveryDetails.promiseLabel"
                            />
                        </div>
                    </div>
                    <div class="d-flex justify-content-center pb-2">
                        <ob1-boutons
                            :is-button="false"
                            :href="dto.bonusRecoveryDetails.link"
                            :is-secondary="true"
                            target="_blank"
                            :label="dto.bonusRecoveryDetails.linkLabelShort"
                            :accessibility-text="dto.bonusRecoveryDetails.linkAccessibilityLabel"
                            data-oevent-label="vers_selecteur_avec_filtre_bonus"
                            data-oevent-category="ajout_bonus_reprise"
                            data-oevent-action="decouvrir_notre_selection"
                        />
                    </div>
                </div>
            </div>

            <ob1-expanding-accordion
                class="bg-gray2 px-1 mb-2"
                element-type="h3"
                :link-title="dto.bonusRecoveryExample.title"
                link-id="bonus-button"
                collapsed-block-id="bonus-detail"
            >
                <recovery-bonus-part :bonus-details="dto.bonusRecoveryExample" />
                <div class="d-flex justify-content-center text-mw">
                    <ob1-liens
                        :href="dto.bonusRecoveryDetails.link"
                        target="_blank"
                        :text="dto.bonusRecoveryDetails.linkLabelShort"
                        :accessibility-text="dto.bonusRecoveryDetails.linkAccessibilityLabel"
                        :with-arrow="true"
                        data-oevent-label="vers_selecteur_avec_filtre_bonus"
                        data-oevent-category="exemple_bonus"
                        data-oevent-action="decouvrir_notre_selection"
                    />
                </div>
            </ob1-expanding-accordion>

            <!--Bloc Faq-->
            <section v-if="dto.faq">
                <h2 class="my-2" v-html="dto.faq.title" />
                <div class="accordion mb-1">
                    <div
                        v-for="(textBlock, index) in dto.faq.questionList"
                        :key="'faq-'+index"
                    >
                        <ob1-expanding-accordion-card
                            element-type="h3"
                            :collapsed-block-id="'faq-text-' + index"
                            :link-id="'faq-link-' + index"
                            :link-title="textBlock.title"
                            :is-card-body-max-width="false"
                        >
                            <div v-html="textBlock.text" />
                        </ob1-expanding-accordion-card>
                    </div>
                </div>
            </section>
        </div>
        <div class="bg-gray2">
            <div class="container-fluid">
                <!--/* Mentions légales */-->
                <mobile-recovery-legal-mentions
                    :title="dto.legalMentions.title"
                    :messages="dto.legalMentions.messages"
                    :mentions="dto.legalMentions.mentions"
                />
                <!--/* Zone Kit mains libres */-->
                <free-hands :text="dto.freeHandsText" />
            </div>
        </div>
        <!--/* Zone Fil d'Ariane */-->
        <div class="d-webview-none" :class="isSoshShop ? 'bg-gray2 py-1' : 'bg-dark py-1'">
            <div class="container-fluid">
                <ob1-breadcrumb :aria-label="'Fil d\'ariane'" :is-inverse="!isSoshShop" :items="dto.filArianeDTO.items" />
            </div>
        </div>

        <!-- snack bar pour afficher le message de confirmation d'enregistrement de la simulation de reprise -->
        <ob1-snack-bar
            id="simulation-snackbar"
            ref="savedSimulationSnackbar"
            icon="icon-tick-circle"
            :label="dto.recoverySimulator.result.snackbarLabel"
        />
    </div>
</template>

<script>
import {defineComponent} from "vue";
import FreeHands from "../../../common/components/FreeHands.vue";
import ListChronologicalOrder from "../../../common/components/ob1/ListChronologicalOrder.vue";
import ListIcon from "../../../common/components/ob1/ListIcon.vue";
import MobileRecovery from "../mobilerecovery/MobileRecovery.vue";
import MobileRecoveryLegalMentions from "./MobileRecoveryLegalMentions.vue";
import MobileRecoverySimulationResultModal from "../MobileRecoverySimulationResultModal.vue";
import MobileRecoverySimulatorModal from "../MobileRecoverySimulatorModal.vue";
import RecoveryBonusPart from "../RecoveryBonusPart.vue";
import {scrollToElement} from "../Util";

const mobileLandingRecoveryDto = JSON.parse(document.getElementById('mobile-landing-recovery').dataset.mobileLandingRecovery);

export default defineComponent({
    components: {
        FreeHands,
        ListChronologicalOrder,
        ListIcon,
        MobileRecovery,
        MobileRecoveryLegalMentions,
        MobileRecoverySimulationResultModal,
        MobileRecoverySimulatorModal,
        RecoveryBonusPart
    },
    data: function () {
        return {
            dto: mobileLandingRecoveryDto,
            manualEstimation: mobileLandingRecoveryDto.recoveryEstimation.manualEstimation,
            estimatedDevice: {
                amountInCents: undefined,
                deviceModel: undefined
            },
            savedEstimatedDevice: {
                amountInCents: mobileLandingRecoveryDto.recoveryEstimation.amountInCents,
                deviceModel: mobileLandingRecoveryDto.recoveryEstimation.deviceModel
            },
            simulatorModalId: "mobile-recovery-simulator",
            currentSimulationResultModalId: "current-mobile-recovery-simulation-result"
        }
    },
    computed: {
        isSoshShop() {
            return this.dto.shop === 'SOSH'
        }
    },
    methods: {
        updateRecoveryPrice(priceInCents, model) {
            this.estimatedDevice.amountInCents = priceInCents;
            this.estimatedDevice.deviceModel = model;
            $("#" + this.simulatorModalId).modal('hide');
            $("#" + this.currentSimulationResultModalId).modal('show');
        },
        newSimulation() {
            $("#" + this.currentSimulationResultModalId).modal('hide');
            $("#" + this.simulatorModalId).modal('show');
        },
        saveSimulation() {
            this.savedEstimatedDevice.amountInCents = this.estimatedDevice.amountInCents;
            this.savedEstimatedDevice.deviceModel = this.estimatedDevice.deviceModel;
            this.manualEstimation = true;
            this.scrollToPageMenuAnchor('landing-recovery');
            this.showSavedSimulationSnackbar();
        },
        showSavedSimulationSnackbar() {
            const snackbar = this.$refs.savedSimulationSnackbar;
            if (snackbar) {
                snackbar.show();
            }
        },
        scrollToPageMenuAnchor: function (id) {
            const element = document.getElementById(id);
            if (element) {
                scrollToElement(element, {dx: 0, dy: 0});
            }
        }
    }
})

</script>

<style lang="scss">
@import 'ob1_common';
@import 'ob1_variables';

p#online-details, p#store-details {
    ul.list {
        margin-bottom: 0;
    }
}

#landing-recovery {
    @media(min-width: #{map-get($grid-breakpoints, lg)}) {
        max-height: px-to-rem(340);
    }
}

#list-icon-landing-recovery,
#list-chronological-landing-online-recovery,
#list-chronological-landing-store-recovery {
    .items-list-item .item-text {
        font-size: inherit;
    }
}

</style>
